import { Col, Skeleton } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { match } from "ts-pattern";

import { Booking } from "../../../../types/bookings";
import DashboardLayout from "../../../molecules/DashboardLayout";
import { type CommercialRenewalStepId, FormTracker } from "../../../molecules/FormTracker";
import { clearBookingForm, getBookingDetail } from "../../BookingForm/ducks/actions";
import { useBookingSelector } from "../../BookingForm/hooks/useBookingSelector";
import { PaymentPlansStep, PreviewStep, RenewalTermsStep, TorRulesStep } from "./steps";
import type { CommercialRenewalStepProps } from "./steps/types";
import { useCommercialRenewalSteps } from "./useCommercialRenewalSteps";

const getPropertyLabel = (bookingDetails: Booking) => {
  const unitNumber = bookingDetails?.propertyDetails?.unitNumber ?? "";
  const propertyName = bookingDetails?.propertyDetails?.propertyName ?? "";
  return `${unitNumber} ${propertyName}`.trim();
};

const FormTrackerLabel = (props: { label: string }) => {
  const { label } = props;

  return (
    <>
      <Col span={24} className="ft-label-container">
        {label ? (
          <p className="ft-main-label">{label}</p>
        ) : (
          <Skeleton.Input active size="small" style={{ width: 220, height: 28 }} />
        )}
      </Col>
      <Col span={24} className="ft-divider" />
    </>
  );
};

export const CommercialRenewalTerms = () => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const { leaseId = "" } = useParams();
  const bookingDetails = useBookingSelector();
  const { steps, totalSteps } = useCommercialRenewalSteps();
  const [currentStep, setCurrentStep] = useState(1);
  const [paymentPlansInvalidated, setPaymentPlansInvalidated] = useState(false);
  const isSentForApproval = bookingDetails?.renewalStatus === "sent_for_approval";

  const activeStep = steps[currentStep - 1];
  const isLastStep = currentStep === totalSteps;

  useEffect(() => {
    if (leaseId) dispatch(getBookingDetail(leaseId));
    return () => {
      dispatch(clearBookingForm());
    };
  }, [dispatch, leaseId]);

  useEffect(() => {
    if (currentStep > totalSteps) {
      setCurrentStep(totalSteps);
    }
  }, [currentStep, totalSteps]);

  const goNext = () => setCurrentStep((step) => Math.min(step + 1, totalSteps));
  const goBack = () => setCurrentStep((step) => Math.max(step - 1, 1));

  const handleDiscard = () => {
    if (leaseId) navigate(-1);
  };

  const onInvalidatePaymentPlans = useCallback(() => {
    setPaymentPlansInvalidated(true);
  }, []);

  const onPaymentPlansSaved = useCallback(() => {
    setPaymentPlansInvalidated(false);
  }, []);

  const stepProps: CommercialRenewalStepProps = {
    title: activeStep?.title ?? "",
    onDiscard: handleDiscard,
    onNext: goNext,
    onBack: currentStep > 1 ? goBack : undefined,
    isLastStep,
    paymentPlansInvalidated,
    onInvalidatePaymentPlans,
    onPaymentPlansSaved,
    isSentForApproval,
  };

  const renderStepContent = (stepId: CommercialRenewalStepId) =>
    match(stepId)
      .with("renewal-terms", () => <RenewalTermsStep {...stepProps} />)
      .with("payment-plans", () => <PaymentPlansStep {...stepProps} />)
      .with("tor-rules", () => <TorRulesStep {...stepProps} />)
      .with("preview", () => <PreviewStep {...stepProps} />)
      .exhaustive();

  return (
    <DashboardLayout load={bookingDetails?.isLoadingBookingDetails}>
      <Col span={24} className="form-tracker">
        <FormTrackerLabel label={getPropertyLabel(bookingDetails)} />
        <FormTracker steps={steps} currentStep={currentStep} onStepClick={setCurrentStep} />
      </Col>
      {activeStep ? renderStepContent(activeStep.id) : null}
    </DashboardLayout>
  );
};
